import React, { useState, useEffect } from "react";
import { Box, Typography, Paper, Avatar, Stack, Button, IconButton, Badge, Snackbar, Alert } from "@mui/material";
import { motion, AnimatePresence } from "framer-motion";
import {
  Call as CallIcon,
  Person as PersonIcon,
  PhoneInTalk as PhoneInTalkIcon,
  Remove as MinimizeIcon,
  DragIndicator as DragIcon,
  AccessTime as TimeIcon,
} from "@mui/icons-material";
import { useCall } from "../Context/CallContext";
import { useNavigate } from "react-router-dom";

export default function CallModal() {
  // --- 1. SARE HOOKS TOP LEVEL PAR (HAMESHA RETURN SE PEHLE) ---
  const callContext = useCall();
  const navigate = useNavigate();
  const [callDuration, setCallDuration] = useState(0);

  // --- 2. EFFECT HOOK (CONTEXT PAR DEPENDENT) ---
  useEffect(() => {
    let interval;
    // Optional chaining (?) use karein taaki context khali hone par crash na ho
    const status = callContext?.incomingCall?.status;

    if (status === "ANSWERED") {
      interval = setInterval(() => {
        setCallDuration((prev) => prev + 1);
      }, 1000);
    } else if (status === "COMPLETED") {
      clearInterval(interval);
    } else {
      setCallDuration(0);
      clearInterval(interval);
    }
    return () => clearInterval(interval);
  }, [callContext?.incomingCall?.status]);

  // --- 3. EARLY RETURN (AB YAHAN CONDITION CHECK KAREIN) ---
  if (!callContext || Object.keys(callContext).length === 0) {
    return null;
  }

  // Hook call hone ke baad destructuring safe hai
  const { 
    incomingCall, 
    isModalOpen, 
    setIsModalOpen,
    isMinimized, 
    setIsMinimized, 
    handleCreateCallApi, 
    snackbar, 
    setSnackbar 
  } = callContext;

  const handleCloseSnackbar = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
  };

  // Final condition to show UI
  if (!isModalOpen || !incomingCall) return null;

  const isRinging = incomingCall.status === "RINGING";
  const isAnswered = incomingCall.status === "ANSWERED" || incomingCall.status === "UNHOLD";
  const isOnHold = incomingCall.status === "ON_HOLD";
  const isCompleted = incomingCall.status === "COMPLETED";

  const themeColor = isRinging ? "#00D2A0" : 
                     isOnHold ? "#ff9800" : 
                     isCompleted ? "#f44336" : "#2196f3";

  const handleAddService = () => {
    const cleanNumber = incomingCall.src.replace("+91", "").trim();
    setIsMinimized(true);
    navigate("/addservice", { state: { incomingNumber: cleanNumber } });
  };

  return (
    <AnimatePresence>
      <Snackbar
        open={snackbar?.open || false}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar?.severity || "info"}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {snackbar?.message}
        </Alert>
      </Snackbar>

      <Box
        component={motion.div}
        drag
        dragElastic={0.1}
        dragConstraints={{ 
          left: -(window.innerWidth - (isMinimized ? 120 : 380)), 
          right: 0, 
          top: -(window.innerHeight - (isMinimized ? 120 : 450)), 
          bottom: 0 
        }}
        dragMomentum={false}
        initial={{ opacity: 0, scale: 0.9, y: 50 }}
        animate={{
          opacity: 1,
          scale: 1,
          y: 0,
          width: isMinimized ? "auto" : "340px",
        }}
        exit={{ opacity: 0, scale: 0.5 }}
        sx={{
          position: "fixed",
          bottom: "40px",
          right: "40px",
          zIndex: 9999,
          touchAction: "none",
        }}
      >
        {isMinimized ? (
          <Stack direction="row" alignItems="center" spacing={1}>
            {(isAnswered || isOnHold) && (
              <Paper
                elevation={5}
                sx={{
                  px: 1.5,
                  py: 0.5,
                  borderRadius: "12px",
                  bgcolor: "rgba(0,0,0,0.8)",
                  color: "white",
                  backdropFilter: "blur(10px)",
                }}
              >
                <Typography variant="caption" sx={{ fontWeight: 800, fontFamily: "monospace" }}>
                  {formatTime(callDuration)}
                </Typography>
              </Paper>
            )}
            <Paper
              component={motion.div}
              whileHover={{ scale: 1.1 }}
              onClick={() => setIsMinimized(false)}
              className={isRinging ? "ringing-effect" : ""}
              sx={{
                width: 65,
                height: 65,
                borderRadius: "20px",
                cursor: "pointer",
                background: `linear-gradient(135deg, ${themeColor} 0%, #1a1a1a 150%)`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "#fff",
                border: "3px solid #fff",
                boxShadow: "0 10px 25px rgba(0,0,0,0.2)",
              }}
            >
              <PhoneInTalkIcon />
            </Paper>
          </Stack>
        ) : (
          <Paper
            elevation={20}
            sx={{
              borderRadius: "24px",
              overflow: "hidden",
              background: "#fff",
              border: `1px solid ${themeColor}44`,
            }}
          >
            {/* Header / Drag Bar */}
            <Box
              sx={{
                bgcolor: `${themeColor}11`,
                px: 2,
                py: 1,
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Stack direction="row" spacing={1} alignItems="center">
                <DragIcon sx={{ fontSize: 18, color: "#ccc" }} />
                <Typography variant="caption" sx={{ fontWeight: 800, color: themeColor }}>
                  {isRinging && "INCOMING"}
                  {isOnHold && "CALL ON HOLD"}
                  {isAnswered && "LIVE CALL"}
                  {isCompleted && "CALL ENDED"}
                </Typography>
              </Stack>
              <IconButton size="small" onClick={() => setIsMinimized(true)}>
                <MinimizeIcon fontSize="small" />
              </IconButton>
            </Box>

            <Box sx={{ p: 2.5 }}>
              <Stack direction="row" spacing={2} alignItems="flex-start">
                <Badge
                  overlap="circular"
                  anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                  badgeContent={
                    <Box sx={{ width: 12, height: 12, bgcolor: themeColor, borderRadius: "50%", border: "2px solid white" }} />
                  }
                >
                  <Avatar
                    sx={{
                      width: 55,
                      height: 55,
                      background: `linear-gradient(135deg, ${themeColor} 0%, #fff 200%)`,
                      boxShadow: `0 8px 15px ${themeColor}33`,
                    }}
                  >
                    {isRinging ? <CallIcon /> : <PhoneInTalkIcon />}
                  </Avatar>
                </Badge>

                <Box sx={{ flexGrow: 1 }}>
                  <Typography variant="h6" sx={{ fontWeight: 900, fontSize: "1.1rem", color: "#222", lineHeight: 1.2 }}>
                    {incomingCall?.src}
                  </Typography>
                  <Typography variant="body2" sx={{ color: "#888" }}>
                    {isRinging && "Patient Calling..."}
                    {isOnHold && "Call is on hold..."}
                    {isAnswered && "On call..."}
                    {isCompleted && "Call hung up"}
                  </Typography>
                  {(isAnswered || isOnHold) && (
                    <Stack direction="row" spacing={1} alignItems="center">
                      <TimeIcon sx={{ fontSize: 14, color: themeColor }} />
                      <Typography variant="caption" sx={{ fontWeight: 700, color: themeColor }}>
                        Duration: {formatTime(callDuration)}
                      </Typography>
                    </Stack>
                  )}
                </Box>
              </Stack>

              {(isAnswered || isOnHold) && (
                <Stack spacing={1.5} sx={{ mt: 2 }}>
                  <Button
                    fullWidth
                    variant="contained"
                    onClick={handleAddService}
                    startIcon={<PersonIcon />}
                    sx={{
                      py: 1,
                      borderRadius: "12px",
                      bgcolor: "#1a1a1a",
                      textTransform: "none",
                      fontWeight: 700,
                      "&:hover": { bgcolor: "#000" },
                    }}
                  >
                    Add Service Record
                  </Button>
                </Stack>
              )}

              <Box sx={{ mt: 2, pt: 2, borderTop: "1px solid #f0f0f0" }}>
                <Typography variant="caption" sx={{ color: "#bbb", display: "block", textAlign: "center", fontWeight: 600, fontSize: "0.65rem" }}>
                  Spero Home Healthcare Private Limited
                </Typography>
              </Box>
            </Box>
          </Paper>
        )}
      </Box>
    </AnimatePresence>
  );
}